import { isUsOrder } from "@/components/UsFlag";
import { productionSubstage } from "@/constants/roles";

// Stage column sort follows what the badge SHOWS, not just `status`: inside a
// tab every row shares the same status, so the visible sub-state (Waiting for
// reply ▸ Bad photos ▸ Not ready ▸ Photos received · Pending orders ▸ Pending
// check ▸ Check completed · sign / print / sign-off · Packing ready ▸ Complete
// packing · Ready to be shipped …) is what makes the sort meaningful.
const PRODUCTION_SUBSTAGE_RANK = { awaiting: 0, to_print: 1, in_production: 2, signed_off: 3 };
const STAGE_ORDER = ["lead", "pre_order", "pending", "advance", "production", "product_transfer", "packing",
  "inventory_packing", "shipping", "completed", "fulfilled", "na", "flagged", "cancelled", "abandoned"];
const stageSubRank = (o) => {
  const s = (o?.status || "").trim();
  if (o?.cancelled === true || s === "cancelled") return 90;
  if (o?.waiting_reply) return 0;
  if (o?.bad_photos && s === "pre_order") return 1;
  switch (s) {
    case "lead":
    case "pre_order": return !(o.vin || "").trim() ? 2 : o.photos_received ? 4 : 3;
    case "pending": return o.hq_checked ? 3 : o.hq_check_sent ? 2 : 1;
    case "advance": return o.manual_production ? 3 : o.hq_checked ? 2 : 1;
    case "production": return 10 + (PRODUCTION_SUBSTAGE_RANK[productionSubstage(o)] ?? 9);
    case "packing": return o.packed_at ? 2 : 1;
    case "inventory_packing": return o.inventory_packed_at ? 2 : 1;
    case "shipping": return o.shipping_tracking_number ? 2 : 1;
    case "fulfilled": return o.fulfilled_method === "pickup_ready" ? 2 : 1;
    default: return 5;
  }
};
const stageSortValue = (o) => {
  const i = STAGE_ORDER.indexOf((o?.status || "").trim());
  return (i === -1 ? 99 : i) * 100 + stageSubRank(o);
};

/**
 * Column extractors used by OrdersList sorting. Each entry maps a sortKey
 * to a function that pulls the sortable value out of an order object.
 *
 * Keep these in sync with the visible columns in OrdersList.jsx so that
 * every column header can be sorted (business rule Feb 28, 2026: "all the
 * tabs and stations must be able to sort by all the fields in the order line").
 */
export const ORDER_COLUMN_EXTRACTORS = {
  po_number:      (o) => (o?.po_number || "").trim(),
  warehouse:      (o) => (isUsOrder(o) ? "USA" : "CANADA"),
  customer:       (o) => (o?.customer?.name || o?.customer_name || "").trim().toLowerCase(),
  factory:        (o) => (o?.assigned_factory || "").trim(),
  vehicle:        (o) => [o?.brand, o?.model, o?.year].filter(Boolean).join(" ").trim().toLowerCase(),
  brand:          (o) => (o?.brand || "").trim().toLowerCase(),
  model:          (o) => (o?.model || "").trim().toLowerCase(),
  year:           (o) => o?.year ? Number(o.year) : null,
  color:          (o) => (o?.color || "").trim().toLowerCase(),
  catalog:        (o) => (o?.catalog_number || "").trim(),
  codes:          (o) => [o?.front_cover_code, o?.rear_cover_code, o?.f_clamp_code, o?.r_clamp_code].filter(Boolean).join(" ").trim().toLowerCase(),
  logo:           (o) => (o?.logo || "").trim().toLowerCase(),
  stage:          (o) => stageSortValue(o),
  vin:            (o) => (o?.vin || "").trim(),
  rim_size:       (o) => o?.rim_size ? Number(o.rim_size) : null,
  tracking:       (o) => (o?.shipping_tracking_number || "").trim(),
  shipped_on:     (o) => o?.shipping_shipped_at || null,
  eta:            (o) => o?.shipping_eta || null,
  received_on:    (o) => o?.received_at || o?.packing_received_at || null,
  completed_on:   (o) => o?.completed_at || o?.packing_received_at || null,
  bin:            (o) => (o?.bin_number || "").trim(),
  received_by:    (o) => (o?.received_by || "").trim().toLowerCase(),
  cancelled_on:   (o) => o?.cancelled_at || o?.cancel_requested_at || null,
  cancel_reason:  (o) => (o?.cancel_reason || (o?.cancel_reason_labels || []).join(", ") || "").trim().toLowerCase(),
  rush:           (o) => (o?.rush_urgent ? 3 : o?.rush_top ? 2 : o?.rush_highlight ? 1 : Number(o?.rush_level || 0)),
  created_at:     (o) => o?.created_at || null,
  updated_at:     (o) => o?.updated_at || null,
};
